import { api } from "../api/Middleware";
import {
  CUSTOMER_BILLING_PROFILES_LIST_URL,
  CUSTOMER_BILLING_PROFILES_CREATE_WITH_TOKENIZATION_URL,
  CUSTOMER_BILLING_PROFILES_BY_CUSTOMER_URL,
  CUSTOMER_BILLING_PROFILES_DETAIL_URL,
  CUSTOMER_BILLING_PROFILES_UPDATE_URL,
  CUSTOMER_BILLING_PROFILES_DELETE_URL,
  CUSTOMER_BILLING_PROFILES_SET_DEFAULT_URL,
} from "../api/Urls";

// =============================
// CUSTOMER BILLING PROFILES SERVICES
// =============================

// Listar perfis de faturamento
export const listCustomerBillingProfiles = async (params = {}) => {
  return await api.get(CUSTOMER_BILLING_PROFILES_LIST_URL, { params });
};

// Buscar perfis de faturamento por cliente
export const getBillingProfilesByCustomer = async (params = {}) => {
  return await api.get(CUSTOMER_BILLING_PROFILES_BY_CUSTOMER_URL, { params });
};

// Buscar detalhes de um perfil específico
export const getCustomerBillingProfile = async (id) => {
  return await api.get(CUSTOMER_BILLING_PROFILES_DETAIL_URL(id));
};

// Criar novo perfil de faturamento com tokenização
export const createBillingProfileWithTokenization = async (data) => {
  return await api.post(CUSTOMER_BILLING_PROFILES_CREATE_WITH_TOKENIZATION_URL, data);
};

// Atualizar perfil de faturamento
export const updateCustomerBillingProfile = async (id, data) => {
  return await api.put(CUSTOMER_BILLING_PROFILES_UPDATE_URL(id), data);
};

// Deletar perfil de faturamento
export const deleteCustomerBillingProfile = async (id) => {
  return await api.delete(CUSTOMER_BILLING_PROFILES_DELETE_URL(id));
};

// Definir perfil como padrão
export const setDefaultBillingProfile = async (id) => {
  return await api.post(CUSTOMER_BILLING_PROFILES_SET_DEFAULT_URL(id));
};

// =============================
// HELPER FUNCTIONS
// =============================

// Construir parâmetros de busca para a API
export const buildSearchParams = (filters) => {
  const params = {};

  // Paginação
  if (filters.page) params.page = filters.page;
  if (filters.page_size) params.page_size = filters.page_size;

  // Busca
  if (filters.search) params.search = filters.search;

  // Filtros
  if (filters.billing_type && filters.billing_type !== "all") params.billing_type = filters.billing_type;
  if (filters.is_default !== undefined) params.is_default = filters.is_default;
  if (filters.is_active !== undefined) params.is_active = filters.is_active;
  if (filters.customer_profile) params.customer_profile = filters.customer_profile;

  // Ordenação
  if (filters.ordering) params.ordering = filters.ordering;

  return params;
};

// Formatar dados do perfil de faturamento para exibição
export const formatBillingProfileData = (profile) => {
  return {
    ...profile,
    // Status formatado
    statusVariant: profile.is_active ? "success" : "secondary",
    statusText: profile.is_active ? "Ativo" : "Inativo",
    
    // Status padrão formatado
    defaultVariant: profile.is_default ? "primary" : "outline-secondary",
    defaultText: profile.is_default ? "Padrão" : "Secundário",
    
    // Tipo de faturamento formatado
    billingTypeVariant: getBillingTypeVariant(profile.billing_type),
    billingTypeText: profile.billing_type_display || getBillingTypeText(profile.billing_type),
    
    // Datas formatadas
    formattedCreatedAt: profile.created_at 
      ? new Date(profile.created_at).toLocaleDateString("pt-BR")
      : "—",
    formattedUpdatedAt: profile.updated_at 
      ? new Date(profile.updated_at).toLocaleDateString("pt-BR")
      : "—",
    
    // Cartão formatado (se aplicável)
    formattedCardInfo: formatCardInfo(profile),
    
    // Endereço formatado
    formattedAddress: formatAddress(profile),
  };
};

// Obter variante do Bootstrap para tipo de faturamento
export const getBillingTypeVariant = (billingType) => {
  const typeMap = {
    CREDIT_CARD: "primary",
    PIX: "success",
    BOLETO: "warning",
  };
  return typeMap[billingType] || "secondary";
};

// Obter texto legível para tipo de faturamento
export const getBillingTypeText = (billingType) => {
  const typeMap = {
    CREDIT_CARD: "Cartão de Crédito",
    PIX: "PIX",
    BOLETO: "Boleto",
  };
  return typeMap[billingType] || "Não definido";
};

// Formatar informações do cartão
export const formatCardInfo = (profile) => {
  if (profile.billing_type !== "CREDIT_CARD") return null;
  
  const parts = [];
  
  if (profile.card_brand) {
    parts.push(profile.card_brand);
  }
  
  if (profile.card_last_digits) {
    parts.push(`**** ${profile.card_last_digits}`);
  }
  
  return parts.length > 0 ? parts.join(" - ") : "—";
};

// Formatar endereço completo
export const formatAddress = (profile) => {
  const parts = [];
  
  if (profile.address) {
    parts.push(profile.address);
  }
  
  if (profile.address_number) {
    parts.push(profile.address_number);
  }
  
  if (profile.province) {
    parts.push(profile.province);
  }
  
  if (profile.city && profile.state) {
    parts.push(`${profile.city}/${profile.state}`);
  }
  
  if (profile.postal_code) {
    parts.push(`CEP: ${formatPostalCode(profile.postal_code)}`);
  }
  
  return parts.length > 0 ? parts.join(", ") : "—";
};

// Formatar CEP
export const formatPostalCode = (value) => {
  if (!value) return "—";
  
  const clean = value.replace(/\D/g, "");
  
  if (clean.length === 8) {
    return clean.replace(/(\d{5})(\d{3})/, "$1-$2");
  }
  
  return value;
};

// Validar dados do perfil de faturamento antes de enviar para API
export const validateBillingProfileData = (data) => {
  const errors = {};

  if (!data.customer_profile) {
    errors.customer_profile = "Perfil de cliente é obrigatório";
  }

  if (!data.profile_name || data.profile_name.trim().length < 2) {
    errors.profile_name = "Nome do perfil é obrigatório e deve ter pelo menos 2 caracteres";
  }

  if (!data.billing_type) {
    errors.billing_type = "Tipo de faturamento é obrigatório";
  }

  // Validações específicas para cartão de crédito
  if (data.billing_type === "CREDIT_CARD") {
    if (!data.card_holder_name || data.card_holder_name.trim().length < 2) {
      errors.card_holder_name = "Nome do portador é obrigatório";
    }

    if (!data.card_number || data.card_number.replace(/\D/g, "").length < 13) {
      errors.card_number = "Número do cartão é obrigatório e deve ser válido";
    }

    if (!data.card_expiry_month || data.card_expiry_month < 1 || data.card_expiry_month > 12) {
      errors.card_expiry_month = "Mês de expiração inválido";
    }

    if (!data.card_expiry_year || data.card_expiry_year < new Date().getFullYear()) {
      errors.card_expiry_year = "Ano de expiração inválido";
    }

    if (!data.card_ccv || data.card_ccv.length < 3) {
      errors.card_ccv = "CVV é obrigatório e deve ter pelo menos 3 dígitos";
    }
  }

  // Validações de endereço
  if (!data.address || data.address.trim().length < 5) {
    errors.address = "Endereço é obrigatório e deve ter pelo menos 5 caracteres";
  }

  if (!data.address_number || data.address_number.trim().length < 1) {
    errors.address_number = "Número do endereço é obrigatório";
  }

  if (!data.city || data.city.trim().length < 2) {
    errors.city = "Cidade é obrigatória";
  }

  if (!data.state || data.state.length !== 2) {
    errors.state = "Estado é obrigatório (2 caracteres)";
  }

  if (!data.postal_code) {
    errors.postal_code = "CEP é obrigatório";
  } else {
    const clean = data.postal_code.replace(/\D/g, "");
    if (clean.length !== 8) {
      errors.postal_code = "CEP deve ter 8 dígitos";
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

// Verificar se o usuário pode executar uma ação
export const canPerformAction = (action, userContext, profile) => {
  const { user, isOrganization } = userContext;

  // Admin sempre pode tudo
  if (user?.superuser_level === 1) return true;

  // Organização pode gerenciar seus perfis
  if (isOrganization && user?.superuser_level <= 2) return true;

  // Outras verificações específicas por ação
  switch (action) {
    case "view":
      return user?.superuser_level <= 3;
    case "create":
    case "edit":
    case "delete":
    case "set_default":
      return user?.superuser_level <= 2;
    default:
      return false;
  }
};

// Obter anos para seleção de expiração do cartão
export const getExpiryYears = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  
  for (let i = 0; i < 15; i++) {
    years.push(currentYear + i);
  }
  
  return years;
};

// Obter meses para seleção de expiração do cartão
export const getExpiryMonths = () => {
  return [
    { value: 1, label: "01 - Janeiro" },
    { value: 2, label: "02 - Fevereiro" },
    { value: 3, label: "03 - Março" },
    { value: 4, label: "04 - Abril" },
    { value: 5, label: "05 - Maio" },
    { value: 6, label: "06 - Junho" },
    { value: 7, label: "07 - Julho" },
    { value: 8, label: "08 - Agosto" },
    { value: 9, label: "09 - Setembro" },
    { value: 10, label: "10 - Outubro" },
    { value: 11, label: "11 - Novembro" },
    { value: 12, label: "12 - Dezembro" },
  ];
};

// Estados brasileiros
export const getBrazilianStates = () => {
  return [
    { value: "AC", label: "Acre" },
    { value: "AL", label: "Alagoas" },
    { value: "AP", label: "Amapá" },
    { value: "AM", label: "Amazonas" },
    { value: "BA", label: "Bahia" },
    { value: "CE", label: "Ceará" },
    { value: "DF", label: "Distrito Federal" },
    { value: "ES", label: "Espírito Santo" },
    { value: "GO", label: "Goiás" },
    { value: "MA", label: "Maranhão" },
    { value: "MT", label: "Mato Grosso" },
    { value: "MS", label: "Mato Grosso do Sul" },
    { value: "MG", label: "Minas Gerais" },
    { value: "PA", label: "Pará" },
    { value: "PB", label: "Paraíba" },
    { value: "PR", label: "Paraná" },
    { value: "PE", label: "Pernambuco" },
    { value: "PI", label: "Piauí" },
    { value: "RJ", label: "Rio de Janeiro" },
    { value: "RN", label: "Rio Grande do Norte" },
    { value: "RS", label: "Rio Grande do Sul" },
    { value: "RO", label: "Rondônia" },
    { value: "RR", label: "Roraima" },
    { value: "SC", label: "Santa Catarina" },
    { value: "SP", label: "São Paulo" },
    { value: "SE", label: "Sergipe" },
    { value: "TO", label: "Tocantins" },
  ];
};

export default {
  listCustomerBillingProfiles,
  getBillingProfilesByCustomer,
  getCustomerBillingProfile,
  createBillingProfileWithTokenization,
  updateCustomerBillingProfile,
  deleteCustomerBillingProfile,
  setDefaultBillingProfile,
  buildSearchParams,
  formatBillingProfileData,
  getBillingTypeVariant,
  getBillingTypeText,
  formatCardInfo,
  formatAddress,
  formatPostalCode,
  validateBillingProfileData,
  canPerformAction,
  getExpiryYears,
  getExpiryMonths,
  getBrazilianStates,
};